// hooks/usePermissions.ts
import { useAuth } from '../context/AuthContext';
import { UserRole, Permission } from '../types/auth';

export const usePermissions = () => {
  const { 
    hasRole, 
    hasPermission, 
    hasAnyRole, 
    isAdmin, 
    userRoles, 
    user 
  } = useAuth();

  // ✅ Funciones de conveniencia para permisos específicos
  const canViewDashboard = () => hasPermission(Permission.VIEW_DASHBOARD);
  const canViewAccounts = () => hasPermission(Permission.VIEW_ACCOUNTS);
  const canViewClients = () => hasPermission(Permission.VIEW_CLIENTS);
  const canViewReferrals = () => hasPermission(Permission.VIEW_REFERRALS);
  const canViewClaims = () => hasPermission(Permission.VIEW_CLAIMS);
  const canCreateEntry = () => hasPermission(Permission.CREATE_ENTRY);
  const canAccessSettings = () => hasPermission(Permission.ACCESS_SETTINGS);
  const canManageUsers = () => hasPermission(Permission.MANAGE_USERS);

  // ✅ Funciones de conveniencia para roles
  const isUser = () => hasRole(UserRole.USER);
  const isSupervisor = () => hasRole(UserRole.SUPERVISOR);
  const isAdminUser = () => hasRole(UserRole.ADMIN);

  // ✅ Funciones para verificar múltiples roles
  const isStaffOrAdmin = () => hasAnyRole([UserRole.SUPERVISOR, UserRole.ADMIN]);
  const hasElevatedAccess = () => hasAnyRole([UserRole.SUPERVISOR, UserRole.ADMIN]);

  // ✅ Información del usuario actual
  const getCurrentUserInfo = () => ({
    user,
    roles: userRoles,
    isAuthenticated: !!user,
    permissions: {
      canViewDashboard: canViewDashboard(),
      canViewAccounts: canViewAccounts(),
      canViewClients: canViewClients(),
      canViewReferrals: canViewReferrals(),
      canViewClaims: canViewClaims(),
      canCreateEntry: canCreateEntry(),
      canAccessSettings: canAccessSettings(),
      canManageUsers: canManageUsers(),
    }
  });

  return {
    // Funciones básicas del AuthContext
    hasRole,
    hasPermission,
    hasAnyRole,
    isAdmin,
    userRoles,
    user,

    // Funciones de conveniencia para permisos
    canViewDashboard,
    canViewAccounts,
    canViewClients,
    canViewReferrals,
    canViewClaims,
    canCreateEntry,
    canAccessSettings,
    canManageUsers,

    // Funciones de conveniencia para roles
    isUser,
    isSupervisor,
    isAdminUser,
    isStaffOrAdmin,
    hasElevatedAccess,

    // Información completa del usuario
    getCurrentUserInfo,
  };
};